import { useEffect, useMemo, useState } from "react";
import {
    QR_STUDIO_DEFAULTS,
    createQrCode,
    downloadBlob,
    pngBlob,
    svgBlob,
    type ErrorCorrectionLevel,
} from "./qr-studio-core.js";

const CORRECTION_LEVELS: readonly ErrorCorrectionLevel[] = ["L", "M", "Q", "H"];
const EXPORT_SIZES = [512, 1024, 2048] as const;

function DownloadIcon() {
    return (
        <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M12 3v11m0 0 4-4m-4 4-4-4M4 17v3h16v-3" />
        </svg>
    );
}

export function QrStudio() {
    const [settings, setSettings] = useState(QR_STUDIO_DEFAULTS);
    const [pngPending, setPngPending] = useState(false);
    const [exportError, setExportError] = useState<string>();
    const preview = useMemo((): {
        error?: string;
        result?: ReturnType<typeof createQrCode>;
    } => {
        try {
            return { error: undefined, result: createQrCode(settings) };
        } catch (reason) {
            return {
                error:
                    reason instanceof Error
                        ? reason.message
                        : "Could not create QR code.",
            };
        }
    }, [settings]);
    const result = preview.result;
    const error = exportError ?? preview.error;

    useEffect(() => {
        const manifest = document.createElement("link");
        manifest.href = "/qr-studio.webmanifest";
        manifest.rel = "manifest";
        document.head.append(manifest);
        if ("serviceWorker" in navigator)
            void navigator.serviceWorker
                .register("/qr-studio-sw.js")
                .catch(() => undefined);
        return () => manifest.remove();
    }, []);

    function update<K extends keyof typeof settings>(
        key: K,
        value: (typeof settings)[K]
    ) {
        setSettings((current) => ({ ...current, [key]: value }));
    }

    async function exportPng() {
        if (result === undefined) return;
        setPngPending(true);
        try {
            downloadBlob(
                await pngBlob(result.matrix, settings, settings.exportSize),
                "qr-code.png"
            );
        } catch (reason) {
            setExportError(
                reason instanceof Error
                    ? reason.message
                    : "PNG export could not complete."
            );
        } finally {
            setPngPending(false);
        }
    }

    return (
        <main className="qr-studio">
            <header className="qr-header">
                <a className="qr-wordmark" href="/" aria-label="QR Studio home">
                    QR Studio
                </a>
                <p>100% in your browser</p>
            </header>
            <section className="qr-workspace" aria-labelledby="qr-title">
                <div className="qr-controls">
                    <h1 id="qr-title">Make a QR code.</h1>
                    <label className="qr-field qr-content-field">
                        <span>Content</span>
                        <textarea
                            value={settings.value}
                            onChange={(event) =>
                                update("value", event.target.value)
                            }
                            spellCheck={false}
                            aria-describedby="qr-privacy-note"
                        />
                    </label>
                    <fieldset className="qr-field qr-correction-field">
                        <legend>Error correction</legend>
                        <div className="qr-segmented-control">
                            {CORRECTION_LEVELS.map((level) => (
                                <button
                                    className={
                                        settings.errorCorrectionLevel === level
                                            ? "is-active"
                                            : undefined
                                    }
                                    key={level}
                                    type="button"
                                    onClick={() =>
                                        update("errorCorrectionLevel", level)
                                    }
                                    aria-pressed={
                                        settings.errorCorrectionLevel === level
                                    }
                                >
                                    {level}
                                </button>
                            ))}
                        </div>
                    </fieldset>
                    <div className="qr-colours">
                        <label className="qr-field">
                            <span>Foreground</span>
                            <span className="qr-colour-input">
                                <input
                                    aria-label="Foreground colour"
                                    type="color"
                                    value={settings.color}
                                    onChange={(event) =>
                                        update("color", event.target.value)
                                    }
                                />
                                <output>{settings.color}</output>
                            </span>
                        </label>
                        <label className="qr-field">
                            <span>Background</span>
                            <span className="qr-colour-input">
                                <input
                                    aria-label="Background colour"
                                    type="color"
                                    value={settings.background}
                                    onChange={(event) =>
                                        update("background", event.target.value)
                                    }
                                />
                                <output>{settings.background}</output>
                            </span>
                        </label>
                    </div>
                    <label className="qr-field qr-size-field">
                        <span>Size</span>
                        <select
                            value={settings.exportSize}
                            onChange={(event) =>
                                update(
                                    "exportSize",
                                    Number(event.target.value)
                                )
                            }
                        >
                            {EXPORT_SIZES.map((size) => (
                                <option key={size} value={size}>
                                    {size} × {size} px
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
                <div className="qr-output">
                    <div className="qr-preview-shell" aria-live="polite">
                        {result === undefined ? (
                            <p className="qr-preview-error">{error}</p>
                        ) : (
                            <div
                                className="qr-preview"
                                aria-label="Generated QR code"
                                role="img"
                                dangerouslySetInnerHTML={{ __html: result.svg }}
                            />
                        )}
                    </div>
                    <div className="qr-exports">
                        <button
                            className="qr-export qr-export-primary"
                            type="button"
                            disabled={result === undefined}
                            onClick={() =>
                                result !== undefined &&
                                downloadBlob(svgBlob(result.svg), "qr-code.svg")
                            }
                        >
                            <DownloadIcon />
                            Download SVG
                        </button>
                        <button
                            className="qr-export"
                            type="button"
                            disabled={result === undefined || pngPending}
                            onClick={exportPng}
                        >
                            <DownloadIcon />
                            {pngPending ? "Preparing PNG…" : "Download PNG"}
                        </button>
                    </div>
                </div>
            </section>
            <footer id="qr-privacy-note">Nothing is sent anywhere.</footer>
        </main>
    );
}
